import {
    ReactElement,
    useCallback,
    useEffect,
    useState,
    FormEventHandler,
    useMemo
} from 'react';
import { CompanyUser } from '@/api';
import { useCompanyPermissions, useCompaniesTranslation } from '../hooks';
import { useUpdateCompanyRoleAction, CompanyRole } from '../actions';
import {
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISelectInput,
    UISelectValue
} from '@/ui';

type UpdateCompanyRoleModalProps = {
    companyUser: CompanyUser | null;
    ownerCount: number;
    onClose: () => void;
};

export function UpdateCompanyRoleModal(
    props: UpdateCompanyRoleModalProps
): ReactElement {
    const { companyUser, ownerCount, onClose } = props;

    return (
        <UIModal isOpen={companyUser !== null} onClose={onClose}>
            {companyUser && (
                <ModalContent
                    companyUser={companyUser}
                    ownerCount={ownerCount}
                    onClose={onClose}
                />
            )}
        </UIModal>
    );
}

/**
 * Mounted only once a member has been picked, so the action hook has a real
 * user to work from rather than the nullable one the list hands down.
 */
function ModalContent(props: {
    companyUser: CompanyUser;
    ownerCount: number;
    onClose: () => void;
}): ReactElement {
    const { companyUser, ownerCount, onClose } = props;

    const { updateCompanyRole, loading } = useUpdateCompanyRoleAction({
        user: companyUser,
        ownerCount
    });
    const { canEditOwners } = useCompanyPermissions();

    return (
        <UpdateCompanyRoleModalForm
            companyUser={companyUser}
            ownerCount={ownerCount}
            canEditOwners={canEditOwners}
            loading={loading}
            onClose={onClose}
            onSubmit={(role) => {
                updateCompanyRole(role)
                    .then(onClose)
                    .catch(() => {}); // Error handled in hook
            }}
        />
    );
}

type UpdateCompanyRoleModalFormProps = {
    companyUser: CompanyUser;
    ownerCount: number;
    canEditOwners: boolean;
    loading: boolean;
    onClose: () => void;
    onSubmit: (role: CompanyRole) => void;
};

function UpdateCompanyRoleModalForm(
    props: UpdateCompanyRoleModalFormProps
): ReactElement {
    const {
        loading,
        onClose,
        onSubmit,
        canEditOwners,
        ownerCount,
        companyUser
    } = props;
    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    const { t } = useCompaniesTranslation();

    /**
     * Company Roles
     */
    const companyRolesOptions: UISelectValue[] = useMemo(() => {
        const items = [];
        if (canEditOwners) {
            items.push({
                label: t('shared.roles.company.owner.name'),
                subLabel: t('shared.roles.company.owner.description'),
                value: 'owner'
            });
        }
        items.push(
            ...[
                {
                    label: t('shared.roles.company.admin.name'),
                    subLabel: t('shared.roles.company.admin.description'),
                    value: 'admin'
                },
                {
                    label: t('shared.roles.company.billing.name'),
                    subLabel: t('shared.roles.company.billing.description'),
                    value: 'billing'
                },
                {
                    label: t('shared.roles.company.member.name'),
                    subLabel: t('shared.roles.company.member.description'),
                    value: 'member'
                }
            ]
        );
        return items;
    }, [t, canEditOwners]);

    const currentUserRole = companyUser.role;

    const companyRolesDefault =
        companyRolesOptions[companyRolesOptions.length - 1];
    const [companyRoles, setCompanyRoles] = useState<UISelectValue | undefined>(
        companyRolesOptions.find(
            (option) => option.value === currentUserRole
        ) || companyRolesDefault
    );
    const [companyRolesError, setCompanyRolesError] = useState('');

    const checkCompanyRoles = useCallback(
        (selectedCompanyRoles: UISelectValue | undefined): string => {
            if (!selectedCompanyRoles) {
                return t('components.update_company_role_modal.error_required');
            }

            if (
                currentUserRole === 'owner' &&
                selectedCompanyRoles.value !== 'owner' &&
                ownerCount <= 1
            ) {
                return t(
                    'components.update_company_role_modal.error_last_owner'
                );
            }
            return '';
        },
        [currentUserRole, ownerCount, t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setCompanyRolesError(checkCompanyRoles(companyRoles));
        }
    }, [companyRoles, showRealTimeErrors, checkCompanyRoles]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkCompanyRoles(companyRoles)) {
            setShowRealTimeErrors(false);

            let role: CompanyRole = 'member';
            if (companyRoles?.value === 'owner') {
                role = 'owner';
            }
            if (companyRoles?.value === 'admin') {
                role = 'admin';
            }
            if (companyRoles?.value === 'billing') {
                role = 'billing';
            }

            onSubmit(role);
        }
    };

    return (
        <form onSubmit={handleSubmit} noValidate>
            <UIModalHeader
                title={t('components.update_company_role_modal.title')}
            />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <UISelectInput
                        label={t('shared.fields.company_role.label')}
                        placeholder={t(
                            'shared.fields.company_role.placeholder'
                        )}
                        description={t(
                            'components.update_company_role_modal.role_description'
                        )}
                        required={true}
                        options={companyRolesOptions}
                        value={companyRoles}
                        onChange={setCompanyRoles}
                        errorText={showRealTimeErrors ? companyRolesError : ''}
                    />
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'submit',
                        loading: loading,
                        disabled: loading,
                        text: t('shared.labels.save')
                    },
                    {
                        text: t('shared.labels.cancel'),
                        disabled: loading,
                        onClick: onClose
                    }
                ]}
            />
        </form>
    );
}
